/*
 * Compoonent providing a UI view of invoice_t objects.  Depending on the type,
 * this could be an invoice, a bill, or a notice.
 */
import React, { Component } from 'react';
import graphql from 'babel-plugin-relay/macro';
import { QueryRenderer } from 'react-relay';
import { Redirect } from 'react-router-dom';
import AbEnvironment from '../auth/AbEnvironment';
import PropTypes from 'prop-types';
import withStyles from '@mui/styles/withStyles';
import { Button, Divider, Grid, Paper,
         Typography, Table,  TableBody, TableCell, TableHead, TableRow  } from '@mui/material';
import GetApp from '@mui/icons-material/GetApp';

import PageTitle from '../ui/PageTitle';
import LoadingSpinner from '../ui/LoadingSpinner';
import SavingSpinner from '../ui/SavingSpinner';
import ServerError from '../ui/ServerError';
import { format } from 'date-fns';

const abEnvironment = new AbEnvironment();
const InvoiceQuery = graphql`
query InvoiceQuery($invoiceId: UUID!) {
  invoiceTByInvoiceId(invoiceId: $invoiceId) {
    invoiceDate
    invoiceNumber
    invoiceDescription
    invoiceType
    accountTByAccountId {
      accountName
      logo
      contactFirstName
      contactLastName
      contactEmail
      address1
      address2
      city
      state
      country
      postalCode
      invoiceInformation
    }
    accountTByBillToAccountId {
      accountName
      logo
      contactFirstName
      contactLastName
      contactEmail
      address1
      address2
      city
      state
      country
      postalCode
      invoiceInformation
      accountDelegatedAuthsTsByAccountId {
        totalCount
        nodes {
          appName
        }
      }
    }
    invoiceLineTsByInvoiceId {
      totalCount
      nodes {
        invoiceLineId
        itemType
        itemDescription
        atId
        itemAmount
        internalExpense
      }
    }
    invoiceIntegrationTsByInvoiceId { 
      totalCount
      nodes { 
        environment
        entity
        entityId
        paymentEntity
        paymentEntityId
      }
    }
  }
}
`;

const styleSheet = theme => ({
    root: {
      flexGrow: 1,
      //marginTop: 10,
    },
    flex: {
      flex: 1,
    },
    downloadButton: {
      marginTop: '0px' // Adjust to align better with text
      // marginLeft: theme.spacing(1),
      // marginTop: '20px',
      // marginBottom: theme.spacing(1),
    },
    dateField: {
      marginLeft: theme.spacing(1),
      marginRight: theme.spacing(1),
    },
    textField: {
      marginLeft: theme.spacing(1),
      marginRight: theme.spacing(1),
      marginTop: 4,
      width: 180,
    },
    textField2: {
      marginLeft: theme.spacing(1),
      marginRight: theme.spacing(1),
      marginTop: 3,
      width: 160,
    },
    textFieldSearch: {
      marginRight: theme.spacing(1),
      marginTop: 3,
      marginLeft: 0,
      width: 300,
    },
    paper: {
        [theme.breakpoints.up('xs')]: {
          padding: theme.spacing(1),
        },
        [theme.breakpoints.up('sm')]: {
          padding: theme.spacing(2),
        },
        [theme.breakpoints.up('md')]: {
          padding: theme.spacing(3),
        },
        textAlign: 'left',
        // color: theme.palette.text.secondary,
        maxWidth: 1200,
    },    
    tblHead: {
      backgroundColor: '#f1f1f1',
    },
    tblHeadRow: {
      height: 24,
    },
    nonPaperContainer: {
      marginTop: 8
    }  
});

class Invoice extends Component {

  constructor(props) {
    super(props);

    this.state = {
      showButtonSpinner: false,
    }
  }

  /*
  TODO: pdf
  }
  */
  downloadPdf(invoiceId) { 
    const { callPartnerApi } = this.props.auth;

    Promise.resolve()
    .then(() => {

      let aPjwt = abEnvironment.getPjwt();
      let payload = {
        operation: "generateInvoicePDF",
        params: {
          pJwt: aPjwt,
          invoiceId: invoiceId
        }
      };
  
      return callPartnerApi('appbind/generic', 'POST', payload);
    })
    .then(response => {
      return response.json();
    })
    .then(jresp => {
      if (jresp.status === 0) {
        let pdfString = jresp.pdfString;
        let billNumber = jresp.billNumber;
        let downloadName = '' + billNumber + '.pdf';
  
        var hiddenElement = document.createElement('a');
        hiddenElement.href = 'data:application/pdf;base64,' + pdfString;
        hiddenElement.target = '_blank';
        hiddenElement.download = downloadName;
        hiddenElement.click();
      } else {
        // alert("Error: " + jresp.message);
      }

    })
    .catch(err => {
      /*let sbmesg = '';
      if (err.message != null) {
        sbmesg = 'Error in invoice lookup: ' + err.message;
      } else {
        sbmesg = 'Unidentified error in invoice lookup';
      }*/
    });
  }

  syncToAccounting(invoiceId) {
    this.setState({ showButtonSpinner: true });
    const { callPartnerApi } = this.props.auth;
    Promise.resolve()
    .then(() => {

      let aPjwt = abEnvironment.getPjwt();
      let payload = {
        operation: "SyncInvoice",
        params: {
          pJwt: aPjwt,
          invoiceId: invoiceId
        }
      };
  
      return callPartnerApi('appbind/generic', 'POST', payload);
    })
    .then(response => {
      return response.json();
    })
    .then(jresp => {
      this.setState({ showButtonSpinner: false });
      window.location.reload();
    })
    .catch(err => {
      this.setState({ showButtonSpinner: false });
    });

  }

  render() {
    const { isAuthenticatedReseller, isAuthenticatedCustomer, isUnauthenticatedCustomer, needAssent, logError } = this.props.auth;
    const classes = this.props.classes;

    if ((!isAuthenticatedReseller() && !isAuthenticatedCustomer() && !isUnauthenticatedCustomer()) || needAssent()) { 
      return ( <Redirect to='/home' /> );
    }

    const params = new URLSearchParams(this.props.location.search);
    let invoiceId = params.get('invoiceId');

    let backText = "Statement";
    let bt = params.get('bt');
    if (bt != null) {
      backText = bt;
    }

    return (
      <QueryRenderer
        environment={abEnvironment.getEnvironment()}
        query={InvoiceQuery}
        variables={{invoiceId: invoiceId}}
        render={({error, props})=> {
          if (error) {
            logError(error.message);
            return (
              <ServerError/>
            );
          } else if (props) {

            if (props.invoiceTByInvoiceId === null)
            {
              return (
                <PageTitle title="Document not found" showBackButton={true} name={backText} history={this.props.history}/>
              );
            }

            let theInvoice = props.invoiceTByInvoiceId;
            // console.log(JSON.stringify(theInvoice));

            // Set some defaults
            let invoiceNumber      = theInvoice.invoiceNumber;
            let invoiceDescription = theInvoice.invoiceDescription;
            let invoiceDate        = format(new Date(theInvoice.invoiceDate), 'yyyy-MM-dd');

            let totalLabel         = 'Total';
            let netLabel           = 'Net to you';

            // We need the other account for some scenarios
            let account = theInvoice.accountTByAccountId;
            if (account == null) {
              // Set a default
              account = {
                accountName: 'AppBind',
                logo: '/logos/appbind-128t.png',
                contactFirstName: 'Sunir',
                contactLastName: 'Shah',
                contactEmail: 'support@appbind.com',
                address1: '548 Market St',
                address2: 'PMB 65125',
                city: 'San Francisco',
                state: 'CA',
                country: 'US',
                postalCode: '94104-5401'
              };
            }
            let invoiceInformationLines = [];
            if (account.invoiceInformation != null) {
              invoiceInformationLines = account.invoiceInformation.split("\n");
            }

            // BillTo only makes sense in the context of some of the types.
            let billTo = theInvoice.accountTByBillToAccountId;
            let billToInvoiceInformationLines = [];
            if (billTo.invoiceInformation != null) {
              billToInvoiceInformationLines = billTo.invoiceInformation.split("\n");
            }

            // Default the fromAccount to accountTByAccountId, and toAccount to accountTBillToAccountId
            let fromAccount = account;
            let fromAccountInvoiceInformationLines = invoiceInformationLines;
            let toAccount   = billTo;
            let toAccountInvoiceInformationLines = billToInvoiceInformationLines;

            // Sum lines
            let internalAmount = 0;
            let externalAmount = 0;
            let lines = theInvoice.invoiceLineTsByInvoiceId.nodes;
            let externalLines = [];
            let internalLines = [];
            for (let i=0; i<lines.length; i++) {
              internalAmount = internalAmount + Number(lines[i].itemAmount) * -1;

              if (lines[i].internalExpense === 0) {
                externalAmount = externalAmount + Number(lines[i].itemAmount) * -1;
                externalLines.push(lines[i]);
              } else {
                internalLines.push(lines[i]);
              }
            }

            let truncatedInternal = internalAmount.toFixed(2);
            if (truncatedInternal === '-0.00') {
              truncatedInternal = '0.00';
            }

            // Top total
            let externalTotal = Number(externalAmount).toLocaleString("en-US", {style: "currency", currency: "USD", minimumFractionDigits: 2});
            // Net total - for invoices only
            let netTotal = Number(truncatedInternal).toLocaleString("en-US", {style: "currency", currency: "USD", minimumFractionDigits: 2});

            // Show integration push button?
            // - first, are there any accounting integrations?
            let integrationCount = 0;
            if (theInvoice.accountTByBillToAccountId.accountDelegatedAuthsTsByAccountId.totalCount > 0) {
              let ada = theInvoice.accountTByBillToAccountId.accountDelegatedAuthsTsByAccountId;
              for (let i=0; i<ada.nodes.length; i++) {
                let aNode = ada.nodes[i];
                if (aNode.appName === 'Xero' || aNode.appName === 'QuickBooks') {
                  integrationCount++;
                }
              }
            }
            let externalCount = theInvoice.invoiceIntegrationTsByInvoiceId.totalCount;

            // Invoice title
            let invoiceTitle = "Invoice";
            let invoiceSubtitle = null;
            let toTerm = 'To';

            // Show download
            let showDownload = true;

            // Show invoice info lines (e.g. VAT).  This will be turned on for a subset of types.
            let showInvoiceInfoLines = false;

            // ------------------------------------------------------------------
            // In the initial implmentation, we're handling each type of invoice
            // in its own block.  This will eventually be decomposed to separate
            // child components, or merged into a more terse implementation.
            // ------------------------------------------------------------------

            if (theInvoice.invoiceType === 0) {
              // ------------------------------------------------------------------
              // Bill to an account
              // - from AppBind
              // - to account
              // ------------------------------------------------------------------

              let billTerm = this.props.auth.getDocumentTerm('documentsInvoiceType_0_Name');
              if (billTerm === 'documentsInvoiceType_0_Name') {
                  billTerm = 'Bill';
              }

              invoiceTitle = billTerm;
              showInvoiceInfoLines = true;
              toTerm ='Bill to';
            } else if (theInvoice.invoiceType === 1) {
              // ------------------------------------------------------------------
              // Balance funding receipt
              // - from AppBind
              // - to account
              // ------------------------------------------------------------------

              let balanceFundingReceiptTerm = this.props.auth.getDocumentTerm('documentsInvoiceType_1_Name');
              if (balanceFundingReceiptTerm === 'documentsInvoiceType_1_Name') {
                balanceFundingReceiptTerm = 'Balance funding receipt';
              }

              invoiceTitle = balanceFundingReceiptTerm;
              showInvoiceInfoLines = true;
            } else if (theInvoice.invoiceType === 2) {
              // ------------------------------------------------------------------
              // Reseller vendor commission receipt / Invoice
              // - from Account
              // - to Vendor
              // ------------------------------------------------------------------

              let invoiceTerm = this.props.auth.getDocumentTerm('documentsInvoiceType_2_Name');
              if (invoiceTerm === 'documentsInvoiceType_2_Name') {
                invoiceTerm = 'Invoice';
              }

              invoiceTitle = invoiceTerm;
              invoiceSubtitle = "Record of a partner commission paid to you.";
              showInvoiceInfoLines = true;

              // Use the billTo account as the from
              fromAccount = billTo;
              fromAccountInvoiceInformationLines = billToInvoiceInformationLines;

              // Use the from as the billTo
              toAccount = account;
              toAccountInvoiceInformationLines = invoiceInformationLines;
              toTerm = 'Bill to';

              totalLabel = 'Total billed';

            } else if (theInvoice.invoiceType === 3) {
              // ------------------------------------------------------------------
              // Reseller customer markup/service receipt / Invoice
              // - from Account
              // - to Customer
              // ------------------------------------------------------------------

              let invoiceTerm = this.props.auth.getDocumentTerm('documentsInvoiceType_3_Name');
              if (invoiceTerm === 'documentsInvoiceType_3_Name') {
                invoiceTerm = 'Invoice';
              }

              invoiceTitle = invoiceTerm;
              invoiceSubtitle = "Record of a customer payment and associated expenses.";
              showInvoiceInfoLines = true;
              toTerm = 'Bill to';

              // Use the billTo account as the from
              fromAccount = billTo;
              fromAccountInvoiceInformationLines = billToInvoiceInformationLines;

              // Use the account as the billTo
              toAccount = account;
              toAccountInvoiceInformationLines = invoiceInformationLines;

              totalLabel = 'Total billed';

            } else if (theInvoice.invoiceType === 4) {
              // ------------------------------------------------------------------
              // Payout confirmation notification
              // - from AppBind
              // - to account
              // ------------------------------------------------------------------

              let notificationPayoutTerm = this.props.auth.getDocumentTerm('documentsInvoiceType_4_Name');
              if (notificationPayoutTerm === 'documentsInvoiceType_4_Name') {
                notificationPayoutTerm = 'Payout confirmation';
              }

              invoiceTitle = notificationPayoutTerm;
            } else if (theInvoice.invoiceType === 5) {
              // ------------------------------------------------------------------
              // Wallet transfer confirmation notification
              // - from AppBind
              // - to account
              // ------------------------------------------------------------------

              let notificationWalletTransferTerm = this.props.auth.getDocumentTerm('documentsInvoiceType_5_Name');
              if (notificationWalletTransferTerm === 'documentsInvoiceType_5_Name') {
                notificationWalletTransferTerm = 'Wallet transfer confirmation';
              }

              invoiceTitle = notificationWalletTransferTerm;
            } else if (theInvoice.invoiceType === 6) {
              // ------------------------------------------------------------------
              // Balance adjustment confirmation notification
              // - from AppBind
              // - to account
              // ------------------------------------------------------------------

              let notificationBalanceAdjustmentTerm = this.props.auth.getDocumentTerm('documentsInvoiceType_6_Name');
              if (notificationBalanceAdjustmentTerm === 'documentsInvoiceType_6_Name') {
                notificationBalanceAdjustmentTerm = 'Balance adjustment confirmation';
              }

              invoiceTitle = notificationBalanceAdjustmentTerm;
            } else if (theInvoice.invoiceType === 7) {
              // ------------------------------------------------------------------
              // Refund confirmation notification
              // - from AppBind
              // - to account
              // ------------------------------------------------------------------

              let notificationRefundTerm = this.props.auth.getDocumentTerm('documentsInvoiceType_7_Name');
              if (notificationRefundTerm === 'documentsInvoiceType_7_Name') {
                notificationRefundTerm = 'Refund confirmation';
              }

              invoiceTitle = notificationRefundTerm;
            }

            let invoiceInfoLineCounter = 0;

            return (
              <Grid container spacing={3} direction="row" justifyContent="flex-start">
                <Grid item xs={12}>
                  <PageTitle title={invoiceTitle} subtitle={invoiceSubtitle} showBackButton={true} name={backText} noMargin={true} history={this.props.history}/>
                </Grid>
                <Grid item xs={12}>
                  <Paper className={classes.paper}>
                    <Grid container spacing={2} direction="row" justifyContent="flex-start">
                      <Grid item xs={12}>
                        <Grid container direction="row" justifyContent="space-between" alignItems="flex-start">
                          <Grid item>
                            <Grid container direction="column" spacing={0} alignItems="flex-start">
                              <Grid item>
                                <Grid container spacing={1} alignItems="center">
                                  {fromAccount.logo != null &&
                                  <Grid item>
                                  <img alt="FromLogo" height="48px" src={fromAccount.logo} />
                                  </Grid>}
                                  <Grid item>
                                    <Typography variant="h4">{fromAccount.accountName}</Typography>
                                  </Grid>
                                </Grid>
                              </Grid>
                              <Grid item>
                                <Typography variant="body1">{fromAccount.address1}</Typography>
                                {fromAccount.address2 != null &&
                                <Typography variant="body1">{fromAccount.address2}</Typography>}
                                <Typography variant="body1">{fromAccount.city}, {fromAccount.state} {fromAccount.postalCode}</Typography>
                                <Typography variant="body1">{fromAccount.country}</Typography>
                              </Grid>
                            </Grid>
                            {(showInvoiceInfoLines === true) &&
                            <Grid item xs={12}>
                              <Grid container>
                                  {fromAccountInvoiceInformationLines.map(n => {
                                    return (
                                      <Grid item xs={12} key={invoiceInfoLineCounter++}>
                                      <Typography variant="body1">{n}</Typography>
                                      </Grid>
                                    );
                                  })}            
                              </Grid>
                            </Grid>}
                          </Grid>
                          <Grid item>
                            <Grid container spacing={1} alignItems="center">
                              {showDownload === true &&
                              <Grid item>
                                <Button
                                  color="primary"
                                  variant="contained"
                                  className={classes.downloadButton}
                                    onClick={() => 
                                        this.downloadPdf(invoiceId)
                                    }
                                >
                                  Download&nbsp;&nbsp;<GetApp/>
                                </Button>
                              </Grid>}
                              {integrationCount > 0 && externalCount === 0 &&
                              <Grid item>
                                {!this.state.showButtonSpinner &&
                                <Button
                                  color="primary"
                                  variant="contained"
                                  className={classes.downloadButton}
                                    onClick={() => 
                                        this.syncToAccounting(invoiceId)
                                    }
                                >
                                  Sync to Accounting
                                </Button>}
                                {this.state.showButtonSpinner &&
                                <Button color="primary" variant="outlined" className={classes.button}>
                                  <SavingSpinner/>
                                </Button>}
                              </Grid>}
                            </Grid>
                          </Grid>
                        </Grid>
                      </Grid>
                      <Grid item xs={12}>
                        <Grid container spacing={3} direction="row" justifyContent="flex-start" alignItems="baseline">
                          <Grid item>
                            <Grid container direction="column" spacing={0} alignItems="flex-start">
                              <Grid item>
                                <Typography variant="h6">{toTerm}</Typography>
                                <Typography variant="body1">{toAccount.accountName}</Typography>
                                <Typography variant="body1">{toAccount.address1}</Typography>
                                {toAccount.address2 != null &&
                                <Typography variant="body1">{toAccount.address2}</Typography>}
                                <Typography variant="body1">{toAccount.city}, {toAccount.state} {toAccount.postalCode}</Typography>
                                <Typography variant="body1">{toAccount.country}</Typography>
                              </Grid>
                              {(showInvoiceInfoLines === true) &&
                              <React.Fragment>
                                {toAccountInvoiceInformationLines.map(n => {
                                  return (
                                    <Grid item xs={12} key={invoiceInfoLineCounter++}>
                                      <Typography variant="body1">{n}</Typography>
                                    </Grid>
                                  );
                                })}            
                              </React.Fragment>}
                            </Grid>
                          </Grid>
                          <Grid item>
                            <Typography variant="h6">Number</Typography>
                            <Typography variant="body1">{invoiceNumber}</Typography>
                          </Grid>
                          <Grid item>
                            <Typography variant="h6">Date</Typography>
                            <Typography variant="body1">{invoiceDate}</Typography>
                          </Grid>
                        </Grid>
                      </Grid>
                      <Grid item xs={12}><Divider/></Grid>
                      <Grid item xs={12}>
                        <Grid container spacing={3} direction="row" justifyContent="space-between" alignItems="baseline">
                          <Grid item>
                            <Typography variant="h6">Description</Typography>
                            <Typography variant="body1">{invoiceDescription}</Typography>
                          </Grid>
                          <Grid item style={{marginRight: '16px'}}>
                            <Grid container direction="column" justifyContent="flex-start" alignItems="flex-end">
                              <Grid item xs={12}>
                                <Typography variant="h6">{totalLabel}</Typography>
                              </Grid>
                              <Grid item xs={12}>
                                <Typography variant="body1">{externalTotal}</Typography>
                              </Grid>
                            </Grid>
                          </Grid>
                        </Grid>
                      </Grid>
                      <Grid item xs={12}>
                        <Table>
                          <TableHead className={classes.tblHead}>
                            <TableRow className={classes.tblHeadRow}>
                              <TableCell style={{width: 144}}>Item</TableCell> 
                              <TableCell>Description</TableCell> 
                              <TableCell align="right">Amount</TableCell>
                            </TableRow>
                          </TableHead>
                          <TableBody>
                          { externalLines.map(n => {

                            let itemType = n.itemType;
                            let itemDesc = n.itemDescription;
                            let iamt = Number(n.itemAmount) * -1;
                            let itemAmount = Number(iamt).toLocaleString("en-US", {style: "currency", currency: "USD", minimumFractionDigits: 2});

                            return (
                              <TableRow key={n.invoiceLineId}>
                                <TableCell>{itemType}</TableCell>
                                <TableCell>{itemDesc}</TableCell>
                                <TableCell align="right">{itemAmount}</TableCell>
                              </TableRow>
                            );
            
                          })}
                          </TableBody>
                        </Table>
                      </Grid>
                    </Grid>
                  </Paper>
                </Grid>
                {(theInvoice.invoiceType === 2 || theInvoice.invoiceType === 3) && internalLines.length > 0 &&
                <Grid item xs={12}>
                  <Paper className={classes.paper}>
                    <Grid container spacing={2} direction="row" justifyContent="flex-start">
                      <Grid item xs={12}>
                        <Typography variant="h6">Your expenses for this invoice</Typography>
                      </Grid>
                      <Grid item xs={12}>
                        <Table>
                          <TableHead className={classes.tblHead}>
                            <TableRow className={classes.tblHeadRow}>
                              <TableCell style={{width: 144}}>Item</TableCell> 
                              <TableCell>Description</TableCell> 
                              <TableCell align="right">Amount</TableCell>
                            </TableRow>
                          </TableHead>
                          <TableBody>
                          { internalLines.map(n => {

                            let itemType = n.itemType;
                            let itemDesc = n.itemDescription;
                            let iamt = Number(n.itemAmount) * -1;
                            let itemAmount = Number(iamt).toLocaleString("en-US", {style: "currency", currency: "USD", minimumFractionDigits: 2});

                            return (
                              <TableRow key={n.invoiceLineId}>
                                <TableCell>{itemType}</TableCell>
                                <TableCell>{itemDesc}</TableCell>
                                <TableCell align="right">{itemAmount}</TableCell>
                              </TableRow>
                            );
            
                          })}
                          </TableBody>
                        </Table>
                      </Grid>

                      <Grid item xs={12} style={{marginRight: '16px'}}>
                        <Grid container direction="column" justifyContent="flex-start" alignItems="flex-end">
                          <Grid item xs={12}>
                            <Typography variant="h6">{netLabel}</Typography>
                          </Grid>
                          <Grid item xs={12}>
                            <Typography variant="body1">{netTotal}</Typography>
                          </Grid>
                        </Grid>
                      </Grid>
                    </Grid>
                  </Paper>
                </Grid>}
              </Grid>
            );
          }
          return (
            <LoadingSpinner />
          );
        }}
      />
    );
  }
}
  
Invoice.propTypes = {
  classes: PropTypes.object.isRequired,
};
  
export default withStyles(styleSheet)(Invoice);

